import React, { useState, useEffect, useCallback } from 'react';
import { DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckSquare, Square, PlusCircle, MinusCircle, Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

const ManageTeamMembersDialog = ({ team, onSave, onCancel }) => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [teamMemberIds, setTeamMemberIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const getInitials = (name) => {
    if (!name) return '??';
    const names = name.split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return names[0][0].toUpperCase() + names[names.length - 1][0].toUpperCase();
  };
  
  const fetchEmployeesAndTeamMembers = useCallback(async () => {
    if (!team) return;
    setIsLoading(true);
    try {
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('id, name, photo_url, team_id');
      if (employeesError) throw employeesError;
      setAllEmployees(employeesData || []);

      const currentTeamMembers = employeesData.filter(emp => emp.team_id === team.id).map(emp => emp.id);
      setTeamMemberIds(new Set(currentTeamMembers));

    } catch (error) {
      console.error('Error fetching data for member management:', error);
      toast({
        title: 'Error fetching data',
        description: 'Could not load employee list.',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  }, [team, toast]);


  useEffect(() => {
    fetchEmployeesAndTeamMembers();
  }, [fetchEmployeesAndTeamMembers]);

  const handleToggleMember = (employeeId) => {
    setTeamMemberIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(employeeId)) {
        newIds.delete(employeeId);
      } else {
        newIds.add(employeeId);
      }
      return newIds;
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Employees to be removed from this team (set team_id to null)
      const employeesToRemove = allEmployees.filter(emp => emp.team_id === team.id && !teamMemberIds.has(emp.id));
      // Employees to be added or kept in this team (set team_id to team.id)
      const employeesToUpdate = allEmployees.filter(emp => teamMemberIds.has(emp.id));

      const updates = [];

      employeesToRemove.forEach(emp => {
        updates.push(
          supabase.from('employees').update({ team_id: null }).eq('id', emp.id)
        );
      });

      employeesToUpdate.forEach(emp => {
        if (emp.team_id !== team.id) { // Only update if not already in this team
          updates.push(
            supabase.from('employees').update({ team_id: team.id }).eq('id', emp.id)
          );
        }
      });
      
      const results = await Promise.all(updates);
      results.forEach(res => {
        if (res.error) throw res.error;
      });
      
      toast({
        title: 'Success!',
        description: 'Team members updated successfully.',
        className: 'bg-green-600 text-white',
      });
      onSave();
    } catch (error) {
      console.error('Error updating team members:', error);
      toast({
        title: 'Error updating members',
        description: error.message || 'Could not update team members.',
        variant: 'destructive',
      });
    }
    setIsLoading(false);
  };

  const filteredEmployees = allEmployees.filter(emp => 
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100">
      <DialogHeader>
        <DialogTitle className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
          Manage Members for {team?.name}
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          Select employees to add or remove from this team.
        </DialogDescription>
      </DialogHeader>
      
      <div className="py-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input 
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading && !allEmployees.length ? (
           <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-700/50">
            {filteredEmployees.map((employee) => (
              <div 
                key={employee.id} 
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors
                            ${teamMemberIds.has(employee.id) ? 'bg-purple-500/20 hover:bg-purple-500/30' : 'bg-slate-700/50 hover:bg-slate-600/50'}`}
                onClick={() => handleToggleMember(employee.id)}
              >
                <div className="flex items-center">
                  <Avatar className="h-8 w-8 mr-3">
                    <AvatarImage src={employee.photo_url} alt={employee.name} />
                    <AvatarFallback className="text-xs bg-slate-600 text-slate-200">
                      {getInitials(employee.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-slate-100">{employee.name}</span>
                </div>
                {teamMemberIds.has(employee.id) ? (
                  <CheckSquare className="h-6 w-6 text-purple-400" />
                ) : (
                  <Square className="h-6 w-6 text-slate-500" />
                )}
              </div>
            ))}
             {filteredEmployees.length === 0 && !isLoading && (
              <p className="text-slate-400 text-center py-4">No employees found matching "{searchTerm}".</p>
            )}
          </div>
        )}
      </div>

      <DialogFooter className="pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-slate-100">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading} className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white">
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

export default ManageTeamMembersDialog;